<template>
  <div class="base-basic">
    <div class="top-nav">
      <van-icon name="arrow-left" class="back-icon" @click="goBack" />
      <div class="nav-title">配额管理</div>
    </div>

    <div class="body-content">
      <van-pull-refresh v-model="refreshing" @refresh="onRefresh">
        <van-list v-model:loading="loading" :finished="finished" finished-text="没有更多了" @load="onLoad">
          <div class="quota-list">
            <div class="quota-card" v-for="quota in quotaList" :key="quota.id">
              <div class="quota-header">
                <div class="quota-id">配额池 #{{ quota.id }}</div>
                <div class="quota-status" :class="getQuotaStatusClass(quota)">
                  {{ getQuotaStatusText(quota) }}
                </div>
              </div>

              <div class="quota-info">
                <div class="info-row">
                  <span class="label">当前专家:</span>
                  <span class="value">{{ quota.current_expert_name || quota.current_expert_id }}</span>
                </div>
                <div class="info-row">
                  <span class="label">所有专家:</span>
                  <span class="value">{{ quota.all_expert_names ? quota.all_expert_names.join(', ') : quota.all_expert_ids.join(', ') }}</span>
                </div>
                <div class="quota-progress">
                  <div class="progress-label">
                    <span>配额使用情况</span>
                    <span>{{ quota.used_quota }} / {{ quota.total_quota }}</span>
                  </div>
                  <van-progress :percentage="getQuotaPercentage(quota)" :color="getProgressColor(quota)" />
                </div>
                <div class="info-row" v-if="quota.remaining_quota !== undefined">
                  <span class="label">剩余配额:</span>
                  <span class="value">{{ quota.remaining_quota }}</span>
                </div>
                <div class="info-row">
                  <span class="label">创建时间:</span>
                  <span class="value">{{ formatDate(quota.created_time) }}</span>
                </div>
                <div class="info-row">
                  <span class="label">更新时间:</span>
                  <span class="value">{{ formatDate(quota.updated_time) }}</span>
                </div>
              </div>
            </div>
          </div>
        </van-list>
      </van-pull-refresh>

      <van-empty v-if="!loading && quotaList.length === 0 && !hasError" description="暂无配额数据" />
      <van-empty v-if="!loading && hasError" description="加载失败，请下拉刷新重试" image="error" />
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import axios from '@/api/axios';
import { Icon, PullRefresh, List, Progress, Empty, showFailToast } from 'vant';
import moment from 'moment';

const router = useRouter();
const quotaList = ref([]);
const loading = ref(false);
const finished = ref(false);
const refreshing = ref(false);
const hasError = ref(false);

const goBack = () => {
  router.back();
};

const formatDate = (dateStr) => {
  return moment(dateStr).format('YYYY-MM-DD HH:mm:ss');
};

const getQuotaPercentage = (quota) => {
  if (quota.total_quota === 0) return 0;
  return Math.round((quota.used_quota / quota.total_quota) * 100);
};

const getProgressColor = (quota) => {
  const percentage = getQuotaPercentage(quota);
  if (percentage >= 90) return '#f5222d';
  if (percentage >= 70) return '#fa8c16';
  return '#145294';
};

const getQuotaStatusClass = (quota) => {
  const percentage = getQuotaPercentage(quota);
  if (percentage >= 90) return 'status-critical';
  if (percentage >= 70) return 'status-warning';
  return 'status-normal';
};

const getQuotaStatusText = (quota) => {
  const percentage = getQuotaPercentage(quota);
  if (percentage >= 90) return '配额紧张';
  if (percentage >= 70) return '配额不足';
  return '配额充足';
};

const loadQuotaData = async () => {
  try {
    const response = await axios.get('api/experts/quota-pools');
    if (response.data.success && response.data.data) {
      quotaList.value = response.data.data;
      hasError.value = false;
    } else {
      hasError.value = true;
      showFailToast('获取配额数据失败');
    }
  } catch (error) {
    console.error('获取配额数据失败:', error);
    hasError.value = true;
    showFailToast('获取配额数据失败');
  } finally {
    loading.value = false;
    refreshing.value = false;
    finished.value = true;
  }
};

const onLoad = () => {
  if (loading.value || finished.value) {
    return;
  }
  loadQuotaData();
};

const onRefresh = () => {
  finished.value = false;
  loading.value = true;
  quotaList.value = [];
  loadQuotaData();
};

onMounted(() => {
  loading.value = true;
  loadQuotaData();
});
</script>

<style scoped>
.base-basic {
  width: 100%;
  height: 100vh;
  background-color: #f5f5f5;
  display: flex;
  flex-direction: column;
}

.top-nav {
  display: flex;
  align-items: center;
  height: 48px;
  background-color: #fff;
  border-bottom: 1px solid #eee;
  padding: 0 15px;
  position: relative;
}

.back-icon {
  font-size: 20px;
  color: #333;
}

.nav-title {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  font-size: 16px;
  font-weight: 500;
  color: #333;
}

.body-content {
  flex: 1;
  padding: 15px;
  overflow-y: auto;
}

.quota-list {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.quota-card {
  background-color: #fff;
  border-radius: 8px;
  padding: 15px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.quota-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 1px solid #f0f0f0;
}

.quota-id {
  font-size: 16px;
  font-weight: 500;
  color: #333;
}

.quota-status {
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
}

.status-normal {
  background-color: #e6f7ff;
  color: #145294;
}

.status-warning {
  background-color: #fff7e6;
  color: #fa8c16;
}

.status-critical {
  background-color: #fff1f0;
  color: #f5222d;
}

.quota-info {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.info-row .label {
  font-size: 14px;
  color: #666;
}

.info-row .value {
  font-size: 14px;
  color: #333;
  font-weight: 500;
  text-align: right;
  max-width: 60%;
  word-break: break-all;
}

.quota-progress {
  margin-top: 10px;
}

.progress-label {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
  font-size: 14px;
  color: #666;
}

.progress-label span:last-child {
  font-weight: 500;
  color: #333;
}
</style>